<template>
    <Modal @close="close">
        <div class="ds-ex-dialog">
            <div class="ds-glass-dialog-title">Выгрузить отчёт по работе АК</div>
            <div class="ds-ex-dialog-field">
                <span class="ds-glass-label">Магазин</span>
                <ExchangeSelect v-model="departmentId" label="Магазин" placeholder="Выберите магазин" searchable search-placeholder="Найти магазин" :options="departments" :disabled="loading" />
            </div>
            <div class="ds-ex-dialog-field">
                <span class="ds-glass-label">Период</span>
                <div class="ds-glass-date">
                    <AppDateInput :model-value="period" mode="range" presets @update:model-value="onPeriod" />
                </div>
            </div>
            <div v-if="error" class="ds-ex-error" role="alert">{{ error }}</div>
            <div class="ds-glass-dialog-actions">
                <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="loading" @click="close">Отмена</button>
                <button type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="!departmentId || loading" @click="download">{{ loading ? 'Готовим…' : 'Скачать' }}</button>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import Modal from '@/ui/Modal.vue'
import AppDateInput from '@/ui/AppDateInput.vue'
import { useDepartmentStore } from '@/stores/departmentStore'
import ExchangeSelect from './ExchangeSelect.vue'
import { exchangeApi } from './exchangeApi'
import { errorText } from './exchangeActions'
import { exchangeToast } from './exchangeToasts'
import { useExchangeDirectory } from './exchangeDirectory'

const emit = defineEmits<{ close: [] }>()

// POST /api/report/outsource/ — прежние поля department_id/date_from/date_to и имя файла.
const REPORT_NAME = 'Отчет по работе АК'

const departmentStore = useDepartmentStore()
const directory = useExchangeDirectory()

const departments = computed(() => departmentStore.myDepartments.map((item: { id: string, name: string }) => ({ id: item.id, name: item.name })))
const departmentId = ref(directory.profileDepartment.value?.id ?? '')
const period = ref<[Dayjs, Dayjs]>([dayjs().startOf('month'), dayjs().endOf('month').startOf('day')])
const loading = ref(false)
const error = ref('')

function onPeriod(value: Dayjs | [Dayjs, Dayjs] | Dayjs[]) {
    const [start, end] = value as [Dayjs, Dayjs]
    if (start && end) period.value = [start, end]
}

function close() {
    if (!loading.value) emit('close')
}

async function download() {
    const data = {
        department_id: departmentId.value,
        date_from: period.value[0].format('YYYY-MM-DD'),
        date_to: period.value[1].format('YYYY-MM-DD'),
    }
    loading.value = true
    error.value = ''
    try {
        const blob = await exchangeApi.report(data)
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${REPORT_NAME} за ${data.date_from} - ${data.date_to}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        exchangeToast.success('Отчёт выгружен', { text: `${data.date_from} — ${data.date_to}`, key: 'report' })
        emit('close')
    } catch (e) {
        error.value = errorText(e, 'Не удалось выгрузить отчёт')
    } finally {
        loading.value = false
    }
}

onMounted(async () => {
    if (!departmentStore.myDepartments.length) await departmentStore.fetchMyDepartments()
    if (!departmentId.value || !departments.value.some(item => item.id === departmentId.value)) departmentId.value = departments.value[0]?.id ?? ''
})
</script>
